import { useTranslation } from 'react-i18next';

import SummaryAccordion from './components/SummaryAccordion/SummaryAccordion';
import CustomButton from '../../../../../shared/components/Buttons/CustomButton/CustomButton';
import Tooltip from '../../../../../shared/components/Globals/Tooltip/Tooltip';
import { CAN_SEE_BROKER_COMMISSION_FG } from '../../../../../shared/constants/statsig';
import { useFeatureGate } from '../../../../../shared/hooks/useFeatureGate/useFeatureGate';
import { TRANSLATIONS_QUOTES } from '../../../../../shared/translations/quotes';
import { formatCurrency } from '../../../../../shared/utils/currency/currency';
import { addonOptionLabel, type IQuoteSummary } from '../../../../../shared/utils/quotes/quotes';
import './QuoteSummaryPanel.scss';

interface IQuoteSummaryPanelProps {
  prospectName: string;
  summary: IQuoteSummary;
  employees: number;
  isEditing: boolean;
  canSave: boolean;
  saving: boolean;
  saveTooltip?: string;
  submitError?: string | null;
  onSave: () => void;
}

const QuoteSummaryPanel = ({
  prospectName,
  summary,
  employees,
  isEditing,
  canSave,
  saving,
  saveTooltip,
  submitError,
  onSave
}: IQuoteSummaryPanelProps) => {
  const { t } = useTranslation();
  const { isEnabled: canSeeBrokerCommission } = useFeatureGate(CAN_SEE_BROKER_COMMISSION_FG);
  const commissionLines = summary.employerLines.filter((line) => line.commission > 0);

  const totalAmountClass = [
    'quote-summary__total-amount',
    summary.grandTotal === 0 ? 'quote-summary__total-amount--empty' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className="quote-summary">
      <div className="quote-summary__heading">
        <span className="quote-summary__eyebrow">{t(TRANSLATIONS_QUOTES.create.summary.title)}</span>
        <span className="quote-summary__name">{prospectName}</span>
      </div>

      <div className="quote-summary__total">
        <span className="quote-summary__total-label">{t(TRANSLATIONS_QUOTES.create.summary.employerCost)}</span>
        <span className={totalAmountClass}>{formatCurrency(summary.grandTotal)}</span>
        <span className="quote-summary__total-meta">
          {t(TRANSLATIONS_QUOTES.create.summary.employerCostMeta, { count: employees })}
        </span>
      </div>

      <div className="quote-summary__sections">
        <SummaryAccordion
          title={t(TRANSLATIONS_QUOTES.create.summary.costBreakdown)}
          value={formatCurrency(summary.employerTotal)}
        >
          {summary.employerLines.length > 0 ? (
            summary.employerLines.map((line) => (
              <div key={line.key} className="quote-summary__row">
                <span className="quote-summary__row-label">
                  {line.label ? `${line.groupName} (${line.label})` : line.groupName}
                </span>
                <span className="quote-summary__row-value">
                  {line.ratePerEmployeeIsAverage
                    ? formatCurrency(line.total)
                    : `${employees} x ${formatCurrency(line.ratePerEmployee)}`}
                </span>
              </div>
            ))
          ) : (
            <div className="quote-summary__empty">{t(TRANSLATIONS_QUOTES.create.summary.noProducts)}</div>
          )}
        </SummaryAccordion>

        {canSeeBrokerCommission && (
          <SummaryAccordion
            title={t(TRANSLATIONS_QUOTES.create.summary.brokerCommission)}
            value={summary.brokerCommission > 0 ? formatCurrency(summary.brokerCommission) : '—'}
          >
            {commissionLines.length > 0 ? (
              commissionLines.map((line) => (
                <div key={line.key} className="quote-summary__row">
                  <span className="quote-summary__row-label">
                    {line.label} ({Math.round(line.commissionPct * 100)}%)
                  </span>
                  <span className="quote-summary__row-value">{formatCurrency(line.commission)}</span>
                </div>
              ))
            ) : (
              <div className="quote-summary__empty">
                {summary.employerLines.length > 0 || summary.voluntaryLines.length > 0
                  ? t(TRANSLATIONS_QUOTES.create.summary.commissionIncluded)
                  : t(TRANSLATIONS_QUOTES.create.summary.noProducts)}
              </div>
            )}
          </SummaryAccordion>
        )}

        {summary.voluntaryLines.length > 0 && (
          <SummaryAccordion
            title={t(TRANSLATIONS_QUOTES.create.summary.employeeFunded)}
            value={t(TRANSLATIONS_QUOTES.create.summary.employeeFundedCount, { count: summary.voluntaryLines.length })}
          >
            {summary.voluntaryLines.map((line) => (
              <div key={line.key} className="quote-summary__row">
                <span className="quote-summary__row-label">{line.groupName}</span>
                <span className="quote-summary__row-value">{addonOptionLabel(line.groupName, line.label)}</span>
              </div>
            ))}
          </SummaryAccordion>
        )}
      </div>

      {submitError && <div className="quote-summary__error">{submitError}</div>}

      <div className="quote-summary__actions">
        <Tooltip content={saveTooltip ?? ''} disabled={canSave || !saveTooltip} placement="top">
          <CustomButton
            variant="primary"
            size="l"
            disabled={!canSave}
            loading={saving}
            onClick={onSave}
            className="quote-summary__save"
          >
            {isEditing ? t(TRANSLATIONS_QUOTES.create.summary.update) : t(TRANSLATIONS_QUOTES.create.summary.save)}
          </CustomButton>
        </Tooltip>
      </div>
    </aside>
  );
};

export default QuoteSummaryPanel;
